/**
 * Lightweight GeoJSON metadata extraction for drag-drop preflight.
 * Reads CRS + geographic bbox without pulling in a full geo library.
 */

import { normalizeCrsCode } from '@/crs/crsCatalog';
import type { PreflightCrs } from './preflightTypes';

export interface GeoJsonMetadata {
  readonly crs?: PreflightCrs;
  readonly bbox?: [number, number, number, number];
}

function parseCrsFromGeoJson(doc: Record<string, unknown>): PreflightCrs | undefined {
  const crs = doc.crs;
  if (crs && typeof crs === 'object') {
    const props = (crs as { properties?: { name?: string } }).properties;
    const name = props?.name;
    if (typeof name === 'string' && name.trim()) {
      return {
        code: normalizeCrsCode(name),
        coordinateMode: 'georeferenced',
      };
    }
  }
  return {
    code: 'EPSG:4326',
    coordinateMode: 'georeferenced',
  };
}

function walkCoords(
  value: unknown,
  acc: { minX: number; minY: number; maxX: number; maxY: number } | null,
): { minX: number; minY: number; maxX: number; maxY: number } | null {
  if (!Array.isArray(value)) return acc;
  if (value.length >= 2 && typeof value[0] === 'number' && typeof value[1] === 'number') {
    const [x, y] = value;
    if (!Number.isFinite(x) || !Number.isFinite(y)) return acc;
    if (!acc) return { minX: x, minY: y, maxX: x, maxY: y };
    return {
      minX: Math.min(acc.minX, x),
      minY: Math.min(acc.minY, y),
      maxX: Math.max(acc.maxX, x),
      maxY: Math.max(acc.maxY, y),
    };
  }
  let next = acc;
  for (const entry of value) {
    next = walkCoords(entry, next);
  }
  return next;
}

function bboxFromFeatures(features: unknown[]): [number, number, number, number] | undefined {
  let acc: { minX: number; minY: number; maxX: number; maxY: number } | null = null;
  for (const feature of features) {
    if (!feature || typeof feature !== 'object') continue;
    const geometry = (feature as { geometry?: { coordinates?: unknown } }).geometry;
    if (!geometry) continue;
    acc = walkCoords(geometry.coordinates, acc);
  }
  if (!acc || acc.maxX <= acc.minX || acc.maxY <= acc.minY) return undefined;
  return [acc.minX, acc.minY, acc.maxX, acc.maxY];
}

/** Parse GeoJSON text for CRS hint and geographic bbox (best-effort). */
export function extractGeoJsonMetadata(text: string): GeoJsonMetadata {
  try {
    const doc = JSON.parse(text) as Record<string, unknown>;
    const crs = parseCrsFromGeoJson(doc);
    const features = Array.isArray(doc.features) ? doc.features : [];
    const bbox = bboxFromFeatures(features);
    return { crs, bbox };
  } catch {
    return {};
  }
}
